import { StyledMunicipalBlock, StyledProjectList } from './style';
import type { ProjectInterface } from '../../temporary-data';
import { useIntl } from 'react-intl';
import { ProjectListItem } from '../ProjectListItem';
import { Collapse, Divider, Label, Pagination, scrollToTop } from 'ui';
import { Collapse as AntdCollapse, Flex } from 'antd';
import usePagination from 'utils/usePagination';
import dayjs from 'dayjs';
import { type ComponentPropsWithoutRef, useEffect, useState } from 'react';
import { useSystemSettingState } from 'contexts/SystemSettingContext';
import type { State as ParticipationBudgetState } from 'contexts/ParticipationBudgetContext';
import { SpecialistInfo } from '../../SpecialistInfo';
import Chart from '../../../../../../../../ui/chart';

type BlockType = 'first' | 'second' | 'third';

interface ProjectsListProps extends ComponentPropsWithoutRef<'div'> {
  data: {
    projects: ProjectInterface[];
  };
  municipalProject: ParticipationBudgetState['budgets'][number];
  municipalInfo: {
    submitted_projects: number;
    vote_count: number;
    realized_projects: number;
    projects_in_voting: number;
    participants_in_voting: number;
    in_person_votes: number;
    being_implemented_projects: number;
    supported_projects: number;
    budget: number;
    voting_period_from: string;
    voting_period_to: string;
    submission_period_from: string;
    submission_period_to: string;
    daily_voting_distribution: DailyVotingDistributionProps[];
    contact_person_name?: string;
    contact_person_email?: string;
    contact_person_phone?: string;
  };
}

interface DailyVotingDistributionProps {
  date: string;
  votes: number;
}

export const ProjectsList = ({ data, municipalProject, municipalInfo }: ProjectsListProps) => {
  const [seeMore, setSeeMore] = useState(false);

  const intl = useIntl();
  const { Panel } = AntdCollapse;
  const { currentPage, paginatedData, handlePageChange } = usePagination(data.projects);
  const {
    device: { isDesktop },
  } = useSystemSettingState();

  const itemCount = data.projects.length;

  useEffect(() => scrollToTop(isDesktop), [currentPage]);

  const renderBlockList = (type: BlockType) => {
    const list = paginatedData;

    return list.length ? (
      <div className={`${type}_block block`}>
        <div className="projects">
          {list.map((project: ProjectInterface) => (
            <ProjectListItem
              key={project.id}
              project={project}
              visibleVotes={municipalProject?.visible_votes}
              votingPeriodFrom={municipalProject?.voting_period_from}
              votingPeriodTo={municipalProject?.voting_period_to}
              isVotingPeriod={municipalProject?.vote_available}
              renderLayout={
                isDesktop
                  ? undefined
                  : ({ image, title, voteBtn, seeMore, voteCount }) => (
                      <Flex vertical className="project-list-item w-full" gap={20}>
                        <Flex gap={20} className="card-header">
                          {image()}
                          {title()}
                        </Flex>
                        <Flex justify="space-between" className="card-actions">
                          {voteBtn()}
                          {seeMore()}
                        </Flex>
                        <div className="vote-count"></div>
                        {voteCount()}
                      </Flex>
                    )
              }
            />
          ))}
        </div>
      </div>
    ) : null;
  };

  const renderBudgetInfo = () => {
    const dates = municipalInfo?.daily_voting_distribution?.map((item) => dayjs(item.date).format('DD.MM.YYYY.'));
    const votes = municipalInfo?.daily_voting_distribution?.map((item) => item.votes);

    return (
      <>
        <Collapse onChange={() => setSeeMore((old) => !old)} className="less-margin show-more">
          <Panel
            forceRender
            key={'show-more'}
            header={<label>{<b>{intl.formatMessage({ id: 'projects.statistics_and_information' })}</b>}</label>}
          >
            <Flex vertical gap={8}>
              {!!municipalInfo.submitted_projects && (
                <StyledMunicipalBlock>
                  <Label label={intl.formatMessage({ id: 'participation_budget.project_information' })} bold subTitle />
                  <div>
                    {(municipalInfo?.submitted_projects || municipalInfo?.submitted_projects === 0) && (
                      <Label
                        label={intl.formatMessage(
                          { id: 'participation_budget.submitted_count' },
                          { count: <b>&nbsp;{municipalInfo.submitted_projects}</b> }
                        )}
                      />
                    )}
                    {(municipalInfo?.projects_in_voting || municipalInfo?.projects_in_voting === 0) && (
                      <Label
                        label={intl.formatMessage(
                          { id: 'participation_budget.voting_count' },
                          { count: <b>&nbsp;{municipalInfo.projects_in_voting}</b> }
                        )}
                      />
                    )}
                    {(municipalInfo?.supported_projects || municipalInfo?.supported_projects === 0) && (
                      <Label
                        label={intl.formatMessage(
                          { id: 'participation_budget.supported_count' },
                          { count: <b>&nbsp;{municipalInfo.supported_projects}</b> }
                        )}
                      />
                    )}
                    {(municipalInfo?.being_implemented_projects || municipalInfo?.being_implemented_projects === 0) && (
                      <Label
                        label={intl.formatMessage(
                          { id: 'participation_budget.being_implemented_count' },
                          { count: <b>&nbsp;{municipalInfo.being_implemented_projects}</b> }
                        )}
                      />
                    )}
                    {(municipalInfo?.realized_projects || municipalInfo?.realized_projects === 0) && (
                      <Label
                        label={intl.formatMessage(
                          { id: 'participation_budget.realized_count' },
                          { count: <b>&nbsp;{municipalInfo.realized_projects}</b> }
                        )}
                      />
                    )}
                  </div>
                </StyledMunicipalBlock>
              )}

              {!!municipalInfo.vote_count && (
                <StyledMunicipalBlock>
                  <Label label={intl.formatMessage({ id: 'participation_budget.voting_information' })} bold subTitle />
                  <div>
                    {(municipalInfo?.vote_count || municipalInfo?.vote_count === 0) && (
                      <Label
                        label={intl.formatMessage(
                          { id: 'participation_budget.vote_count' },
                          { count: <b>&nbsp;{municipalInfo.vote_count}</b> }
                        )}
                      />
                    )}
                    {(municipalInfo?.participants_in_voting || municipalInfo?.participants_in_voting === 0) && (
                      <Label
                        label={intl.formatMessage(
                          { id: 'participation_budget.participants_in_voting' },
                          { count: <b>&nbsp;{municipalInfo.participants_in_voting}</b> }
                        )}
                      />
                    )}
                    {(municipalInfo?.in_person_votes || municipalInfo?.in_person_votes === 0) && (
                      <Label
                        label={intl.formatMessage(
                          { id: 'participation_budget.in_person_votes' },
                          { count: <b>&nbsp;{municipalInfo.in_person_votes}</b> }
                        )}
                      />
                    )}
                    <Chart
                      labels={dates}
                      values={votes}
                      label={intl.formatMessage({ id: 'general.received_vote_count' })}
                    />
                  </div>
                </StyledMunicipalBlock>
              )}

              {!!municipalInfo.budget && (
                <StyledMunicipalBlock>
                  <Label label={intl.formatMessage({ id: 'participation_budget.budget_information' })} bold subTitle />
                  <div>
                    {municipalInfo?.budget && (
                      <Label
                        label={intl.formatMessage(
                          { id: 'amount_allocated_to_participation_budget' },
                          {
                            count: (
                              <b>
                                &nbsp;
                                {new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 2 })
                                  .format(municipalInfo.budget)
                                  .replace(',', '.')}{' '}
                                &nbsp;EUR
                              </b>
                            ),
                          }
                        )}
                      />
                    )}
                    {municipalInfo?.submission_period_from && (
                      <Label
                        label={intl.formatMessage(
                          { id: 'project.project_submission_period' },
                          {
                            period: (
                              <b>
                                &nbsp;
                                {dayjs(municipalInfo?.submission_period_from).format('DD.MM.YYYY.') +
                                  ' - ' +
                                  dayjs(municipalInfo?.submission_period_to).format('DD.MM.YYYY.')}
                              </b>
                            ),
                          }
                        )}
                      />
                    )}
                    {municipalProject?.voting_period_from && (
                      <Label
                        label={intl.formatMessage(
                          { id: 'project.voting_period' },
                          {
                            period: (
                              <b>
                                &nbsp;
                                {dayjs(municipalInfo?.voting_period_from).format('DD.MM.YYYY.') +
                                  ' - ' +
                                  dayjs(municipalInfo?.voting_period_to).format('DD.MM.YYYY.')}
                              </b>
                            ),
                          }
                        )}
                      />
                    )}
                    <Divider className="my-4" />
                    <SpecialistInfo
                      hasSubmissionPeriod
                      isThereSubmissionPeriod={false}
                      isMunicipalLoading={false}
                      budget={municipalProject}
                      hideButton
                      customParams={{
                        contact_person_name: municipalInfo?.contact_person_name,
                        contact_person_email: municipalInfo?.contact_person_email,
                        contact_person_phone: municipalInfo?.contact_person_phone,
                      }}
                    />
                  </div>
                </StyledMunicipalBlock>
              )}
            </Flex>
          </Panel>
        </Collapse>
      </>
    );
  };

  return (
    <StyledProjectList>
      <div className="municipal_project_list">
        <div>
          <div className="default_block">
            {renderBudgetInfo()}
            {renderBlockList('first')}
          </div>
        </div>
      </div>
      {itemCount > 6 && (
        <Pagination
          current={currentPage}
          onChange={handlePageChange}
          total={data.projects.length}
          showSizeChanger={false}
          className="default"
        />
      )}
    </StyledProjectList>
  );
};
